import type { Command } from "@shared-core/domain/object-ui/contract";
import { Button } from "../primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../primitives/dropdown-menu";

/**
 * The commands there is no room to show as buttons, behind one "More…"
 * button. A disabled command stays in the menu with its reason under it: a
 * phone has no hover to show one, and the reason is part of the item's text
 * so a screen reader reads it with the label. `unavailable` commands (a child
 * row's, `rowCommandsOf`) come last under "Not available now", as the record
 * header folds its own.
 */
export function MoreCommands({
  commands,
  unavailable = [],
  align = "start",
  onRun,
}: {
  commands: readonly Command[];
  unavailable?: readonly Command[];
  /** Which edge of the button the menu lines up with: a table's last column opens it to the left. */
  align?: "start" | "end";
  onRun: (command: Command) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="max-sm:h-10">
          More…
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="max-w-[min(20rem,90vw)]">
        {commands.map((command) => (
          <CommandItem key={command.name} command={command} onRun={onRun} />
        ))}
        {unavailable.length > 0 && (
          <>
            {commands.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Not available now
            </DropdownMenuLabel>
            {unavailable.map((command) => (
              <CommandItem key={command.name} command={command} onRun={onRun} />
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CommandItem({
  command,
  onRun,
}: {
  command: Command;
  onRun: (command: Command) => void;
}) {
  return (
    <DropdownMenuItem
      disabled={!command.enabled}
      onSelect={() => onRun(command)}
      className="flex-col items-start gap-0.5 py-2"
    >
      <span>{command.label}</span>
      {!command.enabled && command.reason && (
        <span className="text-xs text-amber-700 dark:text-amber-400">
          {command.reason}
        </span>
      )}
    </DropdownMenuItem>
  );
}
